import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowUpRight,
  BookOpenCheck,
  BrainCircuit,
  Briefcase,
  Building2,
  Calendar,
  Linkedin,
  MapPin,
  Target,
} from 'lucide-react';
import './Resume.css';

const skills = [
  {
    icon: Target,
    title: 'Market Research',
    copy: 'Primary interviews, secondary research, competitive mapping, JTBD, and consumer insight synthesis.',
    items: ['Qualitative interviewing', 'Euromonitor', 'Competitor analysis', 'Segmentation'],
  },
  {
    icon: Briefcase,
    title: 'Strategy',
    copy: 'Practical recommendations for market entry, positioning, product strategy, and growth campaigns.',
    items: ['Market entry', 'Brand repositioning', 'Marketing mix', 'Executive decks'],
  },
  {
    icon: BrainCircuit,
    title: 'Analytics & AI',
    copy: 'Turning messy signals into persuasive recommendations with SPSS, Qualtrics, Excel, and AI workflows.',
    items: ['SPSS', 'Qualtrics', 'Excel', 'Synthetic users'],
  },
];

const experiences = [
  {
    role: 'IME Marketing Consultant',
    company: 'Daddl Emirates',
    location: 'Abu Dhabi, UAE',
    period: 'March 2026 - June 2026',
    highlights: [
      'Analyzed India through qualitative interviews and Euromonitor Passport research.',
      'Built a market-entry strategy for a UAE-based date-candy company.',
      'Presented mode-of-entry and digital marketing recommendations to C-level leadership.',
    ],
  },
  {
    role: 'Marketing Consultant',
    company: 'Resurrection College Prep High School',
    location: 'Chicago, IL',
    period: 'March 2026 - May 2026',
    highlights: [
      'Used JTBD to study the Chicagoland high-school market and competitor landscape.',
      'Converted SPSS and Excel analysis into repositioning and product strategy recommendations.',
    ],
  },
  {
    role: 'IME Marketing Consultant',
    company: 'Bosch Rexroth',
    location: 'Lincolnshire, IL',
    period: 'January 2026 - March 2026',
    highlights: [
      'Researched agricultural OEMs with Qualtrics and AI-augmented synthetic-user methods.',
      'Created a B2B marketing campaign grounded in positioning analysis.',
      'Presented campaign strategy to senior executives.',
    ],
  },
  {
    role: 'IME Marketing Consultant',
    company: 'Ferrara Candy',
    location: 'Chicago, IL',
    period: 'September 2025 - November 2025',
    highlights: [
      'Mapped the fruit-snacks market with industry, competitor, and JTBD analysis.',
      'Used Claritas Prizm clusters and qualitative interviews for targeting strategy.',
      'Recommended positioning and marketing-mix ideas to drive adoption.',
    ],
  },
];

const metrics = [
  { value: '5+', label: 'Client strategy projects' },
  { value: '3.85', label: 'GPA at DePaul' },
  { value: '2026', label: 'Marketing degree' },
];

function Resume() {
  return (
    <main className="resume-page">
      <section className="portfolio-hero" aria-labelledby="hero-title">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          className="hero-copy"
        >
          <div className="eyebrow">Marketing Honors Consultant</div>
          <h1 id="hero-title">David Lukin turns market noise into brand strategy.</h1>
          <p className="hero-lede">
            Consumer engagement, IME Honors marketing, strategic insights, and AI-augmented research for brands that need clear decisions.
          </p>
          <div className="hero-actions">
            <a href="#experience" className="primary-action">
              View Experience
              <ArrowUpRight size={18} />
            </a>
            <a
              href="https://www.linkedin.com/in/david-lukin/"
              target="_blank"
              rel="noopener noreferrer"
              className="secondary-action"
            >
              <Linkedin size={18} />
              LinkedIn
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.12, duration: 0.55 }}
          className="hero-visual"
        >
          <img src="/Work.jpg" alt="David Lukin" />
        </motion.div>
      </section>

      <section className="metric-strip" aria-label="Portfolio highlights">
        {metrics.map((metric) => (
          <div key={metric.label} className="metric-item">
            <strong>{metric.value}</strong>
            <span>{metric.label}</span>
          </div>
        ))}
      </section>

      <section className="section-grid about-section section-panel">
        <div className="section-focus">
          <p className="section-kicker">About</p>
          <h2>Dependable, curious, and built for practical recommendations.</h2>
        </div>
        <p>
          I am a Marketing Honors student with a background in consulting, strategy, and market research. My work focuses on analyzing business challenges, finding useful patterns in consumer behavior, and turning research into recommendations teams can actually act on.
        </p>
      </section>

      <section className="skills-section" aria-labelledby="skills-title">
        <div className="section-heading section-heading-centered">
          <p className="section-kicker">Capabilities</p>
          <h2 id="skills-title">A research-to-strategy toolkit.</h2>
        </div>
        <div className="skills-grid">
          {skills.map((skill, index) => {
            const Icon = skill.icon;
            return (
              <motion.article
                key={skill.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="skill-category"
              >
                <div className="skill-icon">
                  <Icon size={24} />
                </div>
                <h3>{skill.title}</h3>
                <p>{skill.copy}</p>
                <div className="skill-tags">
                  {skill.items.map((item) => (
                    <span key={item}>{item}</span>
                  ))}
                </div>
              </motion.article>
            );
          })}
        </div>
      </section>

      <section id="experience" className="experience-section" aria-labelledby="experience-title">
        <div className="section-heading">
          <p className="section-kicker">Experience</p>
          <h2 id="experience-title">Client work across candy, education, and B2B manufacturing.</h2>
        </div>
        <div className="timeline">
          {experiences.map((job) => (
            <motion.article
              key={`${job.company}-${job.period}`}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              className="experience-item"
            >
              <div className="experience-topline">
                <div>
                  <h3>{job.role}</h3>
                  <p className="company">
                    <Building2 size={16} />
                    {job.company}
                  </p>
                </div>
                <div className="experience-meta">
                  <span>
                    <Calendar size={15} />
                    {job.period}
                  </span>
                  <span>
                    <MapPin size={15} />
                    {job.location}
                  </span>
                </div>
              </div>
              <ul>
                {job.highlights.map((highlight) => (
                  <li key={highlight}>{highlight}</li>
                ))}
              </ul>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="education-band">
        <div>
          <p className="section-kicker">Education</p>
          <h2>Bachelor's Degree in Marketing</h2>
          <p>DePaul University, IME Honors Marketing Program, Digital Marketing Concentration.</p>
        </div>
        <div className="education-stats">
          <span>GPA 3.85</span>
          <span>Graduation June 2026</span>
        </div>
      </section>

      <section className="work-links" aria-label="Explore portfolio work">
        <div>
          <p className="section-kicker">Selected Work</p>
          <h2>Research, writing, and strategic thinking in one place.</h2>
        </div>
        <div className="work-link-actions">
          <Link to="/projects" className="text-action">
            <Target size={19} />
            Projects
            <ArrowUpRight size={17} />
          </Link>
          <Link to="/papers" className="text-action">
            <BookOpenCheck size={19} />
            Papers
            <ArrowUpRight size={17} />
          </Link>
        </div>
      </section>
    </main>
  );
}

export default Resume;
